"use client"

import {
  BadgeCheck,
  Bell,
  Building2,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Sparkles,
  User,
  LogIn,
} from "lucide-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

interface NavUserProps {
  user?: {
    name: string
    email: string
    avatar?: string
    organization?: string
    organizationRole?: string
  }
  subscription?: {
    type: string
    status: string
    hasAvailableSubscription: boolean
    hasOpusPlanDefault: boolean
    recommendedSubscription: string
    isQualifiedForDataSharing: boolean
  }
  isAuthenticated?: boolean
  onLogin?: () => void
  onLogout?: () => void
}

export function NavUser({
  user,
  subscription,
  isAuthenticated = false,
  onLogin,
  onLogout,
}: NavUserProps) {
  const { isMobile } = useSidebar()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user?.avatar} alt={user?.name || 'User'} />
                <AvatarFallback className="rounded-lg">
                  {isAuthenticated && user ? 
                    user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 
                    <User className="h-4 w-4" />
                  }
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {isAuthenticated && user ? user.name : 'Claude User'}
                </span>
                <span className="truncate text-xs">
                  {isAuthenticated && user ? user.email : 'Authenticate in terminal'}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user?.avatar} alt={user?.name || 'User'} />
                  <AvatarFallback className="rounded-lg">
                    {isAuthenticated && user ? 
                      user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 
                      <User className="h-4 w-4" />
                    }
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {isAuthenticated && user ? user.name : 'Claude User'}
                  </span>
                  <span className="truncate text-xs">
                    {isAuthenticated && user ? user.email : 'Authenticate in terminal'}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
                 <DropdownMenuGroup>
                   <DropdownMenuItem>
                     <Sparkles />
                     {subscription ? (
                       <>
                         Byte DevOps {subscription.type}
                         {subscription.status !== 'Active' && (
                           <span className="ml-2 text-xs text-muted-foreground">
                             ({subscription.status})
                           </span>
                         )}
                       </>
                     ) : (
                       'Byte DevOps'
                     )}
                   </DropdownMenuItem>
                   {subscription?.organization && (
                     <DropdownMenuItem>
                       <Building2 className="mr-2 h-4 w-4" />
                       {subscription.organization}
                       {subscription.organizationRole && (
                         <span className="ml-2 text-xs text-muted-foreground">
                           ({subscription.organizationRole})
                         </span>
                       )}
                     </DropdownMenuItem>
                   )}
                 </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <BadgeCheck />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuItem>
                <CreditCard />
                Subscription
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Bell />
                Notifications
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            {isAuthenticated ? (
              <DropdownMenuItem onClick={onLogout}>
                <LogOut />
                Log out
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={onLogin}>
                <LogIn />
                Authenticate
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
